import React, { useEffect, useState, useRef, useCallback } from 'react';
import { storiesAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import Avatar from '../UI/Avatar';

export default function StatusTab() {
  const { user }               = useAuth();
  const [groups, setGroups]    = useState([]);
  const [loading, setLoading]  = useState(true);
  const [viewer, setViewer]    = useState(null); // {group, storyIndex}
  const [showPost, setShowPost] = useState(false);

  const loadStories = useCallback(async () => {
    try {
      const res = await storiesAPI.list();
      if (res.data.success) setGroups(res.data.data.stories);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadStories();
    // Refresh statuses every 30s so new updates appear automatically
    const iv = setInterval(loadStories, 30000);
    return () => clearInterval(iv);
  }, [loadStories]);

  const openViewer = (group, idx = 0) => setViewer({ group, idx });

  const myGroup = groups.find(g => g.user_id == user?.id);

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {/* My status */}
      <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 700, color: '#667781', textTransform: 'uppercase', letterSpacing: '.5px' }}>
        My Status
      </div>
      <div className="conv-item" onClick={() => myGroup ? openViewer(myGroup) : setShowPost(true)}>
        <div style={{ position: 'relative' }}>
          <Avatar user={user} size={48} />
          {!myGroup && (
            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 18, height: 18, background: '#128C7E', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'white', border: '2px solid white' }}>+</div>
          )}
        </div>
        <div className="conv-info">
          <div className="conv-name">My Status</div>
          <div className="conv-preview">
            {myGroup ? `${myGroup.stories.length} update(s)` : 'Tap to add status update'}
          </div>
        </div>
        <button className="icon-btn dark" onClick={e => { e.stopPropagation(); setShowPost(true); }}>✏️</button>
      </div>

      {/* Others */}
      {groups.filter(g => g.user_id != user?.id).length > 0 && (
        <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 700, color: '#667781', textTransform: 'uppercase', letterSpacing: '.5px' }}>
          Recent Updates
        </div>
      )}
      {groups.filter(g => g.user_id != user?.id).map(group => (
        <div key={group.user_id} className="conv-item" onClick={() => openViewer(group)}>
          <div style={{ position: 'relative' }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%', padding: 2,
              background: group.all_viewed
                ? 'linear-gradient(#ccc, #ccc)'
                : 'linear-gradient(45deg, #25D366, #128C7E)',
            }}>
              <Avatar user={{ name: group.user_name, avatar: group.user_avatar }} size={44}
                style={{ border: '2px solid white', borderRadius: '50%' }} />
            </div>
          </div>
          <div className="conv-info">
            <div className="conv-name">{group.user_name}</div>
            <div className="conv-preview">
              {formatDistanceToNow(new Date(group.stories[0]?.created_at), { addSuffix: true })}
            </div>
          </div>
        </div>
      ))}

      {loading && <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" /></div>}

      {/* Story viewer */}
      {viewer && (
        <StoryViewer group={viewer.group} startIdx={viewer.idx}
          onClose={() => { setViewer(null); loadStories(); }}
          isOwn={viewer.group.user_id == user?.id} />
      )}

      {/* Post status modal */}
      {showPost && <PostStoryModal onClose={() => { setShowPost(false); loadStories(); }} />}
    </div>
  );
}

// ── Story Viewer ──────────────────────────────────────────────
function StoryViewer({ group, startIdx, onClose, isOwn }) {
  const [idx, setIdx]           = useState(startIdx);
  const [progress, setProgress] = useState(0);
  const intervalRef             = useRef(null);
  const story                   = group.stories[idx];
  const DURATION                = 5000;

  useEffect(() => {
    if (story) storiesAPI.view(story.id).catch(() => {});
    setProgress(0);
    intervalRef.current = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          if (idx < group.stories.length - 1) { setIdx(i => i + 1); return 0; }
          else { onClose(); return 100; }
        }
        return p + (100 / (DURATION / 100));
      });
    }, 100);
    return () => clearInterval(intervalRef.current);
  }, [idx]);

  if (!story) return null;

  return (
    <div className="story-viewer-overlay">
      <div className="story-viewer-content">
        {/* Progress bars */}
        <div className="story-progress-bar">
          {group.stories.map((s, i) => (
            <div key={s.id} className="story-progress-item">
              <div className="story-progress-fill" style={{ width: i < idx ? '100%' : i === idx ? progress + '%' : '0%' }} />
            </div>
          ))}
        </div>

        {/* User info */}
        <div className="story-user-info">
          <Avatar user={{ name: group.user_name, avatar: group.user_avatar }} size={40} />
          <div>
            <p>{group.user_name}</p>
            <span>{formatDistanceToNow(new Date(story.created_at), { addSuffix: true })}</span>
          </div>
          <button onClick={onClose} className="icon-btn" style={{ marginLeft: 'auto', color: 'white' }}>✕</button>
          {isOwn && (
            <button className="icon-btn" style={{ color: 'white' }}
              onClick={() => storiesAPI.delete(story.id).then(onClose)}>🗑️</button>
          )}
        </div>

        {/* Story content */}
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {story.file_url && story.type === 'image' && (
            <img src={story.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          {story.file_url && story.type === 'video' && (
            <video src={story.file_url} autoPlay muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          {story.type === 'text' && (
            <div style={{ background: story.bg_color || '#128C7E', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
              <p style={{ color: 'white', fontSize: 24, fontWeight: 700, textAlign: 'center', lineHeight: 1.4 }}>{story.content}</p>
            </div>
          )}
        </div>

        {/* Tap zones */}
        <div style={{ position: 'absolute', left: 0, top: 0, width: '40%', height: '100%', zIndex: 5 }}
          onClick={() => idx > 0 ? setIdx(i => i - 1) : onClose()} />
        <div style={{ position: 'absolute', right: 0, top: 0, width: '40%', height: '100%', zIndex: 5 }}
          onClick={() => idx < group.stories.length - 1 ? setIdx(i => i + 1) : onClose()} />
      </div>
    </div>
  );
}

// ── Post Story Modal ──────────────────────────────────────────
const BG_COLORS = ['#128C7E', '#075E54', '#25D366', '#ECE5DD', '#3498DB', '#9B59B6', '#E74C3C', '#F39C12', '#1ABC9C', '#2C3E50'];

function PostStoryModal({ onClose }) {
  const [type, setType]       = useState('text');
  const [content, setContent] = useState('');
  const [bgColor, setBgColor] = useState('#128C7E');
  const [file, setFile]       = useState(null);
  const [posting, setPosting] = useState(false);

  const handlePost = async () => {
    setPosting(true);
    try {
      await storiesAPI.post({ type, content, bg_color: bgColor, ...(file ? { file } : {}) });
      onClose();
    } catch {}
    setPosting(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>📸 Add Status</h2>
          <button className="icon-btn dark" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['text','image','video'].map(t => (
              <button key={t} className={`btn ${type === t ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                style={{ flex: 1 }} onClick={() => setType(t)}>
                {t === 'text' ? '✍️ Text' : t === 'image' ? '📷 Photo' : '🎥 Video'}
              </button>
            ))}
          </div>

          {type === 'text' && (
            <>
              <div style={{ background: bgColor, borderRadius: 12, padding: 24, minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Type your status..."
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: 20, fontWeight: 600, textAlign: 'center', width: '100%', resize: 'none', fontFamily: 'inherit' }}
                  rows={3}
                />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {BG_COLORS.map(c => (
                  <button key={c} onClick={() => setBgColor(c)}
                    style={{ width: 30, height: 30, borderRadius: '50%', background: c, border: c === bgColor ? '3px solid #333' : '2px solid transparent', cursor: 'pointer' }} />
                ))}
              </div>
            </>
          )}

          {(type === 'image' || type === 'video') && (
            <div>
              <input type="file" accept={type === 'image' ? 'image/*' : 'video/*'}
                onChange={e => setFile(e.target.files[0])} className="form-input" style={{ marginBottom: 8 }} />
              {file && <p style={{ fontSize: 13, color: '#667781' }}>Selected: {file.name}</p>}
              <textarea value={content} onChange={e => setContent(e.target.value)}
                placeholder="Add a caption (optional)" className="form-input" rows={2} style={{ marginTop: 8, resize: 'none' }} />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePost} disabled={posting || (!content && !file)}>
            {posting ? '⏳ Posting...' : '✅ Post Status'}
          </button>
        </div>
      </div>
    </div>
  );
}
